import axios from "../../../api/axios";
import { toast } from "react-toastify";

export const INVOICE_ENDPOINTS = {
  INVOICES_BASE: "/v2/invoices/invoice",
  INVOICE_BY_ID: "/v2/invoices/invoice/:id",
  INVOICE_STATUS_COUNTS: "/v2/invoices/invoice/status-counts",
  INVOICE_STATUS: "/v2/invoices/invoice/:id/status",
  INVOICE_COMMENTS: "/v2/invoices/invoice/:id/comments",
  INVOICE_SIGNED_URLS: "/v2/invoices/invoice/signed-urls",
  INVOICE_UPLOAD_REQUEST: "/v2/invoices/invoice/:id/attachments/request-upload",
  INVOICE_CONFIRM_UPLOAD: "/v2/invoices/invoice/:id/attachments/confirm",
  INVOICE_REMOVE_ATTACHMENT: "/v2/invoices/invoice/:id/attachments/remove",
  INVOICE_STAGING_UPLOAD_REQUEST: "/v2/invoices/invoice/attachments/staging/request-upload",
  INVOICE_STAGING_SIGNED_URLS: "/v2/invoices/invoice/attachments/staging/signed-urls",
  INVOICE_STAGING_REMOVE: "/v2/invoices/invoice/attachments/staging/remove",
  INVOICE_FORWARD: "/v2/invoices/invoice/:id/forward",

  CATEGORIES_BASE: "/v2/invoices/category",
  CATEGORY_BY_ID: "/v2/invoices/category/:id",

  VENDORS_BASE: "/v2/invoices/vendor",
  VENDOR_BY_ID: "/v2/invoices/vendor/:id",

  AUDITORS_BASE: "/v2/invoices/auditor",
  AUDITOR_BY_ID: "/v2/invoices/auditor/:id",

  SETTINGS_BASE: "/v2/invoices/settings",
};

const handleApiError = (error, defaultMessage) => {
  const message =
    error.response?.data?.message || error.message || defaultMessage || "Unexpected error";
  console.error(defaultMessage, error);
  toast.error(message);
  throw new Error(message);
};

// ── Invoices ─────────────────────────────────────────────────────────────
export const getInvoices = async (params = {}) => {
  try {
    const res = await axios.get(INVOICE_ENDPOINTS.INVOICES_BASE, { params });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to fetch invoices"); }
};

export const getInvoiceById = async (id) => {
  try {
    const url = INVOICE_ENDPOINTS.INVOICE_BY_ID.replace(":id", id);
    const res = await axios.get(url);
    return res.data.data;
  } catch (e) { handleApiError(e, "Failed to fetch invoice"); }
};

export const getInvoiceStatusCounts = async (params = {}) => {
  try {
    const res = await axios.get(INVOICE_ENDPOINTS.INVOICE_STATUS_COUNTS, { params });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to fetch invoice status counts"); }
};

export const createInvoice = async (payload) => {
  try {
    const res = await axios.post(INVOICE_ENDPOINTS.INVOICES_BASE, payload, {
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to create invoice"); }
};

export const updateInvoice = async (id, payload) => {
  try {
    const url = INVOICE_ENDPOINTS.INVOICE_BY_ID.replace(":id", id);
    const res = await axios.patch(url, payload, {
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to update invoice"); }
};

export const updateInvoiceStatus = async (id, payload) => {
  try {
    const url = INVOICE_ENDPOINTS.INVOICE_STATUS.replace(":id", id);
    const res = await axios.patch(url, payload, {
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to update invoice status"); }
};

export const addInvoiceComment = async (id, payload) => {
  try {
    const url = INVOICE_ENDPOINTS.INVOICE_COMMENTS.replace(":id", id);
    const res = await axios.post(url, payload, {
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to add comment"); }
};

export const deleteInvoice = async (id) => {
  try {
    const url = INVOICE_ENDPOINTS.INVOICE_BY_ID.replace(":id", id);
    const res = await axios.delete(url);
    return res.data;
  } catch (e) { handleApiError(e, "Failed to delete invoice"); }
};

export const forwardInvoice = async (id, payload) => {
  try {
    const url = INVOICE_ENDPOINTS.INVOICE_FORWARD.replace(":id", id);
    const res = await axios.post(url, payload, {
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to forward invoice"); }
};

// ── Attachments ───────────────────────────────────────────────────────────
export const requestInvoiceUploadUrl = async (invoiceId, fileName, contentType) => {
  try {
    const url = INVOICE_ENDPOINTS.INVOICE_UPLOAD_REQUEST.replace(":id", invoiceId);
    const res = await axios.post(url, { fileName, contentType });
    return res.data.data;
  } catch (e) { handleApiError(e, "Failed to obtain upload URL"); }
};

export const confirmInvoiceUpload = async (invoiceId, key) => {
  try {
    const url = INVOICE_ENDPOINTS.INVOICE_CONFIRM_UPLOAD.replace(":id", invoiceId);
    const res = await axios.post(url, { key });
    return res.data.data;
  } catch (e) { handleApiError(e, "Failed to confirm upload"); }
};

export const removeInvoiceAttachment = async (invoiceId, key) => {
  try {
    const url = INVOICE_ENDPOINTS.INVOICE_REMOVE_ATTACHMENT.replace(":id", invoiceId);
    const res = await axios.post(url, { key });
    return res.data.data;
  } catch (e) { handleApiError(e, "Failed to remove attachment"); }
};

export const getInvoiceSignedUrls = async (keys) => {
  if (!Array.isArray(keys) || keys.length === 0) return {};
  try {
    const res = await axios.post(INVOICE_ENDPOINTS.INVOICE_SIGNED_URLS, { keys });
    return res.data.data;
  } catch (e) {
    handleApiError(e, "Failed to generate signed URLs");
    return {};
  }
};

// ── Categories ────────────────────────────────────────────────────────────
export const getInvoiceCategories = async (params = {}) => {
  try {
    const res = await axios.get(INVOICE_ENDPOINTS.CATEGORIES_BASE, { params });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to fetch invoice categories"); }
};

export const createInvoiceCategory = async (payload) => {
  try {
    const res = await axios.post(INVOICE_ENDPOINTS.CATEGORIES_BASE, payload, {
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to create category"); }
};

export const updateInvoiceCategory = async (id, payload) => {
  try {
    const url = INVOICE_ENDPOINTS.CATEGORY_BY_ID.replace(":id", id);
    const res = await axios.patch(url, payload, {
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to update category"); }
};

export const deleteInvoiceCategory = async (id) => {
  try {
    const url = INVOICE_ENDPOINTS.CATEGORY_BY_ID.replace(":id", id);
    const res = await axios.delete(url);
    return res.data;
  } catch (e) { handleApiError(e, "Failed to delete category"); }
};

// ── Vendors ───────────────────────────────────────────────────────────────
export const getInvoiceVendors = async (params = {}) => {
  try {
    const res = await axios.get(INVOICE_ENDPOINTS.VENDORS_BASE, { params });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to fetch vendors"); }
};

export const createInvoiceVendor = async (payload) => {
  try {
    const res = await axios.post(INVOICE_ENDPOINTS.VENDORS_BASE, payload, {
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to create vendor"); }
};

export const updateInvoiceVendor = async (id, payload) => {
  try {
    const url = INVOICE_ENDPOINTS.VENDOR_BY_ID.replace(":id", id);
    const res = await axios.patch(url, payload, {
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to update vendor"); }
};

export const deleteInvoiceVendor = async (id) => {
  try {
    const url = INVOICE_ENDPOINTS.VENDOR_BY_ID.replace(":id", id);
    const res = await axios.delete(url);
    return res.data;
  } catch (e) { handleApiError(e, "Failed to delete vendor"); }
};

// ── Auditors ──────────────────────────────────────────────────────────────
export const getInvoiceAuditors = async (params = {}) => {
  try {
    const res = await axios.get(INVOICE_ENDPOINTS.AUDITORS_BASE, { params });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to fetch auditors"); }
};

export const createInvoiceAuditor = async (payload) => {
  try {
    const res = await axios.post(INVOICE_ENDPOINTS.AUDITORS_BASE, payload, {
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to add auditor"); }
};

export const updateInvoiceAuditor = async (id, payload) => {
  try {
    const url = INVOICE_ENDPOINTS.AUDITOR_BY_ID.replace(":id", id);
    const res = await axios.patch(url, payload, {
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to update auditor"); }
};

export const deleteInvoiceAuditor = async (id) => {
  try {
    const url = INVOICE_ENDPOINTS.AUDITOR_BY_ID.replace(":id", id);
    const res = await axios.delete(url);
    return res.data;
  } catch (e) { handleApiError(e, "Failed to delete auditor"); }
};

// ── Payment Settings ──────────────────────────────────────────────────────
export const getInvoicePaymentSettings = async () => {
  try {
    const res = await axios.get(INVOICE_ENDPOINTS.SETTINGS_BASE);
    return res.data;
  } catch (e) { handleApiError(e, "Failed to fetch payment settings"); }
};

export const saveInvoicePaymentSettings = async (payload) => {
  try {
    const res = await axios.put(INVOICE_ENDPOINTS.SETTINGS_BASE, payload, {
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to save payment settings"); }
};

// ── Helpers ───────────────────────────────────────────────────────────────
export const INVOICE_STATUSES = [
  { value: "Unpaid", color: "#DC2626", bg: "#FEF2F2", border: "#FECDD3" },
  { value: "Pending", color: "#D97706", bg: "#FFFBEB", border: "#FDE68A" },
  { value: "Paid", color: "#059669", bg: "#ECFDF5", border: "#6EE7B7" },
  { value: "Overdue", color: "#B91C1C", bg: "#FEF2F2", border: "#FCA5A5" },
  { value: "Declined", color: "#64748B", bg: "#F1F5F9", border: "#CBD5E1" },
];

export const formatCurrency = (amount, currency = "CAD") => {
  const safe = Number.isFinite(Number(amount)) ? Number(amount) : 0;
  try {
    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: (currency || "CAD").toUpperCase(),
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(safe);
  } catch {
    return `${(currency || "CAD").toUpperCase()} ${safe.toFixed(2)}`;
  }
};

export const uploadFileToInvoice = async (invoiceId, file) => {
  const { signedUrl, key } = await requestInvoiceUploadUrl(invoiceId, file.name, file.type);
  await fetch(signedUrl, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": file.type },
  });
  await confirmInvoiceUpload(invoiceId, key);
  return key;
};

export const requestStagingUploadUrl = async (fileName, contentType) => {
  try {
    const res = await axios.post(INVOICE_ENDPOINTS.INVOICE_STAGING_UPLOAD_REQUEST, { fileName, contentType });
    return res.data.data;
  } catch (e) { handleApiError(e, "Failed to obtain upload URL"); }
};

export const getStagingSignedUrls = async (keys) => {
  if (!Array.isArray(keys) || keys.length === 0) return {};
  try {
    const res = await axios.post(INVOICE_ENDPOINTS.INVOICE_STAGING_SIGNED_URLS, { keys });
    return res.data.data;
  } catch (e) {
    handleApiError(e, "Failed to generate signed URLs");
    return {};
  }
};

export const removeStagingObject = async (key) => {
  try {
    const res = await axios.post(INVOICE_ENDPOINTS.INVOICE_STAGING_REMOVE, { key });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to remove file"); }
};

export const uploadStagingFile = async (file) => {
  const { signedUrl, key } = await requestStagingUploadUrl(file.name, file.type);
  await fetch(signedUrl, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": file.type },
  });
  return key;
};
